import Pusher from 'pusher-js';
import AppModule from '@app/lib/AppModule';
import appManager from '@app/appManager';
import authenticatedFetch from '@api/lib/authenticatedFetch';
import environment from '@lib/environment';
import { AuthEvents } from '@modules/AuthModule/types';

declare global {
  interface Window {
    /** Host-owned Pusher client. Set on auth.signin, cleared on auth.signout. */
    pusherInstance?: Pusher;
  }
}

/**
 * Single generic channel-authorization route owned by contactcenter-bff.
 * Pusher isn't exclusive to CommHub, so this module stays agnostic of any
 * one channel owner -- the backend inspects `channel_name` and dispatches to
 * whichever downstream owns that namespace (CommHub's own
 * `/orgs/{orgId}/pusher/auth` today, others later), resolving org/tenant
 * scope from the caller's own authenticated session rather than the
 * frontend passing it explicitly.
 */
const PUSHER_AUTH_PATH = environment.API_URL + '/v1/pusher/auth';

export class PusherModule extends AppModule {
  override preload(): void {
    this.app.events.on(AuthEvents.SIGNIN, () => {
      this.initialize();
    });

    this.app.events.on(AuthEvents.SIGNOUT, () => {
      this.teardown();
    });
  }

  private initialize(): void {
    if (!environment.PUSHER_APP_KEY) {
      this.app.logger.warn('Pusher not initialized: missing app key');
      return;
    }

    if (window.pusherInstance) {
      return;
    }

    const pusher = new Pusher(environment.PUSHER_APP_KEY, {
      cluster: environment.PUSHER_CLUSTER,
      channelAuthorization: {
        customHandler: (params, callback) => {
          authenticatedFetch(PUSHER_AUTH_PATH, {
            method: 'POST',
            headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
            body: `socket_id=${params.socketId}&channel_name=${encodeURIComponent(params.channelName)}`,
          })
            .then((response) => response.json())
            .then((data) => callback(null, data))
            .catch((error) =>
              callback(
                error instanceof Error ? error : new Error(String(error)),
                null,
              ),
            );
        },
      },
    });

    pusher.connection.bind('error', (error: unknown) => {
      this.app.logger.error('Pusher connection error', { error });
    });

    window.pusherInstance = pusher;
    this.app.logger.info('Pusher initialized');
  }

  private teardown(): void {
    window.pusherInstance?.disconnect();
    window.pusherInstance = undefined;
  }
}

export default new PusherModule(appManager.events, appManager.logger('Pusher'));
